import React, { useState, useEffect } from 'react';
import { FaPlus, FaTrash, FaInfoCircle, FaTimes, FaCheck, FaCalendar, FaPercent, FaDollarSign } from 'react-icons/fa';

const PM_FIELD =
  'w-full rounded-lg border border-[#504945] bg-[#3c3836] px-3 py-2 text-sm text-[#fbf1c7] placeholder:text-[#928374] focus:border-[#83a598] focus:outline-none focus:ring-2 focus:ring-[#83a598]';

const PM_FIELD_COMPACT =
  'w-full rounded border border-[#504945] bg-[#3c3836] px-2 py-1.5 text-sm text-[#fbf1c7] placeholder:text-[#928374] focus:border-[#83a598] focus:outline-none focus:ring-1 focus:ring-[#83a598]';

const PromoPricingManager = ({ basePrice, promotions = [], onChange, error = '' }) => {
  const [localPromotions, setLocalPromotions] = useState([]);
  const [showMultipleCodes, setShowMultipleCodes] = useState({});

  useEffect(() => {
    // Normalize promotions - handle both single object and array
    if (Array.isArray(promotions) && promotions.length > 0) {
      setLocalPromotions(promotions);
    } else if (promotions && typeof promotions === 'object' && promotions.isActive !== undefined) {
      setLocalPromotions([promotions]);
    } else {
      setLocalPromotions([]);
    }
  }, [promotions]);

  // Calculate sale price from discount
  const calculateSalePrice = (promo) => {
    if (!promo.discountType || !promo.discountValue || promo.discountValue <= 0) {
      return basePrice;
    }

    if (promo.discountType === 'percentage') {
      const discountAmount = (basePrice * promo.discountValue) / 100;
      return Math.round((basePrice - discountAmount) * 100) / 100;
    } else if (promo.discountType === 'fixed') {
      return Math.max(0, Math.round((basePrice - promo.discountValue) * 100) / 100);
    }

    return basePrice;
  };

  // Check if promo dates are valid
  const validatePromoDates = (promo) => {
    if (!promo.startDate || !promo.endDate) return true;
    return new Date(promo.startDate) < new Date(promo.endDate);
  };

  // Check if promo is currently active
  const isPromoActive = (promo) => {
    if (!promo.isActive) return false;
    const now = new Date();
    const start = promo.startDate ? new Date(promo.startDate) : null;
    const end = promo.endDate ? new Date(promo.endDate) : null;

    if (start && now < start) return false;
    if (end && now > end) return false;
    return true;
  };

  const handleAddPromotion = () => {
    const newPromo = {
      isActive: true,
      salePrice: null,
      discountType: 'percentage',
      discountValue: 0,
      originalPrice: basePrice,
      startDate: '',
      endDate: '',
      promoCode: '',
      promoCodes: [],
      description: '',
      autoRevert: true,
      customerPromoDurationMonths: 6, // Default: 6 months from customer join date
      eligibilityCriteria: {
        newCustomersOnly: true,
        minContractMonths: 0,
        maxDiscounts: null
      }
    };
    const updated = [...localPromotions, newPromo];
    setLocalPromotions(updated);
    onChange(updated);
  };

  const handleRemovePromotion = (index) => {
    const updated = localPromotions.filter((_, i) => i !== index);
    setLocalPromotions(updated);
    onChange(updated);
  };

  const handlePromoChange = (index, field, value) => {
    const updated = [...localPromotions];
    
    if (field === 'discountType' || field === 'discountValue') {
      updated[index] = {
        ...updated[index],
        [field]: value
      };
      // Auto-calculate sale price
      const newPromo = { ...updated[index], [field]: value };
      updated[index].salePrice = calculateSalePrice(newPromo);
    } else if (field.startsWith('eligibilityCriteria.')) {
      const criteriaField = field.replace('eligibilityCriteria.', '');
      updated[index] = {
        ...updated[index],
        eligibilityCriteria: {
          ...updated[index].eligibilityCriteria,
          [criteriaField]: value
        }
      };
    } else {
      updated[index] = {
        ...updated[index],
        [field]: value
      };
    }

    setLocalPromotions(updated);
    onChange(updated);
  };

  const handleAddPromoCode = (promoIndex) => {
    const updated = [...localPromotions];
    const newCode = {
      code: '',
      description: '',
      discountType: updated[promoIndex].discountType || 'percentage',
      discountValue: updated[promoIndex].discountValue || 0,
      isActive: true,
      usageLimit: null,
      usageCount: 0,
      startDate: updated[promoIndex].startDate || '',
      endDate: updated[promoIndex].endDate || ''
    };
    updated[promoIndex].promoCodes = [...(updated[promoIndex].promoCodes || []), newCode];
    setLocalPromotions(updated);
    onChange(updated);
  };

  const handleRemovePromoCode = (promoIndex, codeIndex) => {
    const updated = [...localPromotions];
    updated[promoIndex].promoCodes = updated[promoIndex].promoCodes.filter((_, i) => i !== codeIndex);
    setLocalPromotions(updated);
    onChange(updated);
  };

  const handlePromoCodeChange = (promoIndex, codeIndex, field, value) => {
    const updated = [...localPromotions];
    updated[promoIndex].promoCodes[codeIndex] = {
      ...updated[promoIndex].promoCodes[codeIndex],
      [field]: value
    };
    setLocalPromotions(updated);
    onChange(updated);
  };

  const toggleMultipleCodes = (index) => {
    setShowMultipleCodes({
      ...showMultipleCodes,
      [index]: !showMultipleCodes[index]
    });
  };

  const getEffectivePrice = () => {
    const activePromos = localPromotions.filter(p => isPromoActive(p));
    if (activePromos.length === 0) return basePrice;
    
    // Use the first active promo
    const promo = activePromos[0];
    return promo.salePrice || calculateSalePrice(promo);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-[#bdae93]">
          Promotional Pricing
        </label>
        <button
          type="button"
          onClick={handleAddPromotion}
          className="inline-flex items-center rounded-lg bg-[#504945] px-3 py-1.5 text-sm font-medium text-[#d3869b] transition-colors hover:bg-[#665c54]"
        >
          <FaPlus className="mr-1.5" size={12} />
          Add Promotion
        </button>
      </div>

      {error && (
        <div className="flex items-start rounded-lg border border-[#cc241d] bg-[#3c1f1f] p-3 text-sm text-[#fb4934]">
          <FaInfoCircle className="mr-2 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Price Preview */}
      {localPromotions.length > 0 && (
        <div className="rounded-lg border border-[#458588] bg-[#32302f] p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-[#ebdbb2]">Effective Price Preview</p>
              <p className="mt-1 text-xs text-[#83a598]">
                Base Price: ${basePrice.toFixed(2)}
              </p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-[#83a598]">
                ${getEffectivePrice().toFixed(2)}
              </p>
              <p className="text-xs text-[#bdae93]">
                {localPromotions.filter(p => isPromoActive(p)).length} active promotion(s)
              </p>
            </div>
          </div>
        </div>
      )}

      {localPromotions.length === 0 ? (
        <div className="rounded-lg border-2 border-dashed border-[#504945] bg-[#32302f] py-8 text-center text-[#928374]">
          <FaPercent className="mx-auto mb-2 h-8 w-8 text-[#665c54]" />
          <p className="text-sm">No promotions configured.</p>
          <p className="text-xs mt-1">Click "Add Promotion" to create promotional pricing.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {localPromotions.map((promo, promoIndex) => {
            const salePrice = promo.salePrice || calculateSalePrice(promo);
            const isValidDates = validatePromoDates(promo);
            const isActive = isPromoActive(promo);
            const discountAmount = basePrice - salePrice;
            const discountPercentage = basePrice > 0 ? (discountAmount / basePrice) * 100 : 0;

            return (
              <div
                key={promoIndex}
                className={`rounded-lg border-2 p-4 ${
                  isActive
                    ? 'border-[#b8bb26] bg-[#32302f]'
                    : isValidDates
                    ? 'border-[#504945] bg-[#282828]'
                    : 'border-[#cc241d] bg-[#3c1f1f]'
                }`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-2">
                    <h4 className="text-sm font-semibold text-[#ebdbb2]">
                      Promotion #{promoIndex + 1}
                    </h4>
                    {isActive && (
                      <span className="inline-flex items-center rounded-full bg-[#3c3c1f] px-2 py-0.5 text-xs font-medium text-[#b8bb26]">
                        <FaCheck className="mr-1" size={8} />
                        Active
                      </span>
                    )}
                    {!isValidDates && (
                      <span className="inline-flex items-center rounded-full bg-[#3c1f1f] px-2 py-0.5 text-xs font-medium text-[#fb4934]">
                        Invalid Dates
                      </span>
                    )}
                  </div>
                  {localPromotions.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemovePromotion(promoIndex)}
                      className="rounded p-1 text-[#fb4934] hover:bg-[#3c1f1f] hover:text-[#cc241d]"
                      title="Remove promotion"
                    >
                      <FaTrash size={14} />
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Enable/Disable Toggle */}
                  <div className="md:col-span-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={promo.isActive || false}
                        onChange={(e) => handlePromoChange(promoIndex, 'isActive', e.target.checked)}
                        className="mr-2 h-4 w-4 rounded border-[#504945] text-[#d3869b] focus:ring-[#83a598]"
                      />
                      <span className="text-sm font-medium text-[#bdae93]">Enable this promotion</span>
                    </label>
                  </div>

                  {/* Discount Type */}
                  <div>
                    <label className="mb-1 block text-xs font-medium text-[#bdae93]">
                      Discount Type *
                    </label>
                    <div className="relative">
                      <select
                        value={promo.discountType || 'percentage'}
                        onChange={(e) => handlePromoChange(promoIndex, 'discountType', e.target.value)}
                        className={PM_FIELD}
                      >
                        <option value="percentage">Percentage (%)</option>
                        <option value="fixed">Fixed Amount ($)</option>
                      </select>
                      {promo.discountType === 'percentage' ? (
                        <FaPercent className="pointer-events-none absolute right-3 top-3 text-[#928374]" />
                      ) : (
                        <FaDollarSign className="pointer-events-none absolute right-3 top-3 text-[#928374]" />
                      )}
                    </div>
                  </div>

                  {/* Discount Value */}
                  <div>
                    <label className="mb-1 block text-xs font-medium text-[#bdae93]">
                      Discount Value *
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max={promo.discountType === 'percentage' ? 100 : basePrice}
                      value={promo.discountValue || ''}
                      onChange={(e) => handlePromoChange(promoIndex, 'discountValue', parseFloat(e.target.value) || 0)}
                      className={PM_FIELD}
                      placeholder={promo.discountType === 'percentage' ? '10' : '10.00'}
                    />
                  </div>

                  {/* Sale Price Display */}
                  <div className="md:col-span-2">
                    <div className="rounded-lg border border-[#504945] bg-[#32302f] p-3">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-[#bdae93]">Calculated Sale Price:</span>
                        <span className="text-lg font-bold text-[#ebdbb2]">
                          ${salePrice.toFixed(2)}
                          {discountPercentage > 0 && (
                            <span className="ml-2 text-sm font-normal text-[#b8bb26]">
                              ({discountPercentage.toFixed(1)}% off)
                            </span>
                          )}
                        </span>
                      </div>
                      <div className="mt-2 flex items-center justify-between text-xs text-[#928374]">
                        <span>Original: ${basePrice.toFixed(2)}</span>
                        <span>Savings: ${discountAmount.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Date Range */}
                  <div>
                    <label className="mb-1 block text-xs font-medium text-[#bdae93]">
                      Start Date
                    </label>
                    <div className="relative">
                      <input
                        type="datetime-local"
                        value={promo.startDate ? promo.startDate.slice(0, 16) : ''}
                        onChange={(e) => handlePromoChange(promoIndex, 'startDate', e.target.value ? `${e.target.value}:00Z` : '')}
                        className={PM_FIELD}
                      />
                      <FaCalendar className="pointer-events-none absolute right-3 top-3 text-[#928374]" />
                    </div>
                  </div>

                  <div>
                    <label className="mb-1 block text-xs font-medium text-[#bdae93]">
                      End Date
                    </label>
                    <div className="relative">
                      <input
                        type="datetime-local"
                        value={promo.endDate ? promo.endDate.slice(0, 16) : ''}
                        onChange={(e) => handlePromoChange(promoIndex, 'endDate', e.target.value ? `${e.target.value}:00Z` : '')}
                        className={PM_FIELD}
                      />
                      <FaCalendar className="pointer-events-none absolute right-3 top-3 text-[#928374]" />
                    </div>
                  </div>

                  {/* Primary Promo Code */}
                  <div>
                    <label className="mb-1 block text-xs font-medium text-[#bdae93]">
                      Primary Promo Code
                    </label>
                    <input
                      type="text"
                      value={promo.promoCode || ''}
                      onChange={(e) => handlePromoChange(promoIndex, 'promoCode', e.target.value.toUpperCase())}
                      className={`${PM_FIELD} font-mono uppercase`}
                      placeholder="SAVE15"
                      maxLength={20}
                    />
                  </div>

                  {/* Multiple Promo Codes Toggle */}
                  <div className="flex items-end">
                    <button
                      type="button"
                      onClick={() => toggleMultipleCodes(promoIndex)}
                      className="w-full rounded-lg border border-[#504945] bg-[#504945] px-3 py-2 text-sm font-medium text-[#d3869b] transition-colors hover:bg-[#665c54]"
                    >
                      {showMultipleCodes[promoIndex] ? 'Hide' : 'Show'} Multiple Codes
                    </button>
                  </div>

                  {/* Multiple Promo Codes Section */}
                  {showMultipleCodes[promoIndex] && (
                    <div className="md:col-span-2 mt-4 border-t border-[#504945] pt-4">
                      <div className="mb-3 flex items-center justify-between">
                        <label className="block text-xs font-medium text-[#ebdbb2]">
                          Additional Promo Codes
                        </label>
                        <button
                          type="button"
                          onClick={() => handleAddPromoCode(promoIndex)}
                          className="inline-flex items-center rounded bg-[#504945] px-2 py-1 text-xs font-medium text-[#d3869b] hover:bg-[#665c54]"
                        >
                          <FaPlus className="mr-1" size={10} />
                          Add Code
                        </button>
                      </div>

                      {(promo.promoCodes || []).length === 0 ? (
                        <p className="text-xs italic text-[#928374]">No additional codes added.</p>
                      ) : (
                        <div className="space-y-2">
                          {promo.promoCodes.map((code, codeIndex) => (
                            <div key={codeIndex} className="rounded-lg border border-[#504945] bg-[#32302f] p-3">
                              <div className="grid grid-cols-1 gap-2 md:grid-cols-4">
                                <input
                                  type="text"
                                  value={code.code || ''}
                                  onChange={(e) => handlePromoCodeChange(promoIndex, codeIndex, 'code', e.target.value.toUpperCase())}
                                  placeholder="CODE"
                                  className={`${PM_FIELD_COMPACT} font-mono uppercase`}
                                  maxLength={20}
                                />
                                <input
                                  type="text"
                                  value={code.description || ''}
                                  onChange={(e) => handlePromoCodeChange(promoIndex, codeIndex, 'description', e.target.value)}
                                  placeholder="Description"
                                  className={PM_FIELD_COMPACT}
                                />
                                <input
                                  type="number"
                                  value={code.usageLimit || ''}
                                  onChange={(e) => handlePromoCodeChange(promoIndex, codeIndex, 'usageLimit', e.target.value ? parseInt(e.target.value) : null)}
                                  placeholder="Usage Limit"
                                  className={PM_FIELD_COMPACT}
                                />
                                <div className="flex items-center space-x-2">
                                  <button
                                    type="button"
                                    onClick={() => handleRemovePromoCode(promoIndex, codeIndex)}
                                    className="rounded p-1.5 text-[#fb4934] hover:bg-[#3c1f1f] hover:text-[#cc241d]"
                                  >
                                    <FaTrash size={12} />
                                  </button>
                                  <label className="flex items-center text-xs text-[#bdae93]">
                                    <input
                                      type="checkbox"
                                      checked={code.isActive !== false}
                                      onChange={(e) => handlePromoCodeChange(promoIndex, codeIndex, 'isActive', e.target.checked)}
                                      className="mr-1 h-3 w-3 rounded border-[#504945] text-[#d3869b]"
                                    />
                                    Active
                                  </label>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Description */}
                  <div className="md:col-span-2">
                    <label className="mb-1 block text-xs font-medium text-[#bdae93]">
                      Promotion Description
                    </label>
                    <textarea
                      value={promo.description || ''}
                      onChange={(e) => handlePromoChange(promoIndex, 'description', e.target.value)}
                      rows="2"
                      className={PM_FIELD}
                      placeholder="Holiday special - 15% off for new customers"
                    />
                  </div>

                  {/* Customer Promo Duration (Months) */}
                  <div>
                    <label className="mb-1 block text-xs font-medium text-[#bdae93]">
                      Customer Promo Duration (Months) *
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="24"
                      value={promo.customerPromoDurationMonths || 6}
                      onChange={(e) => handlePromoChange(promoIndex, 'customerPromoDurationMonths', parseInt(e.target.value) || 6)}
                      className={PM_FIELD}
                      placeholder="6"
                    />
                    <p className="mt-1 text-xs text-[#928374]">
                      How many months each customer gets the promotional price from their join date (not the promotion end date)
                    </p>
                  </div>

                  {/* Eligibility Criteria */}
                  <div className="md:col-span-2 border-t border-[#504945] pt-4">
                    <label className="mb-2 block text-xs font-medium text-[#ebdbb2]">
                      Eligibility Criteria
                    </label>
                    <div className="space-y-2">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={promo.eligibilityCriteria?.newCustomersOnly || false}
                          onChange={(e) => handlePromoChange(promoIndex, 'eligibilityCriteria.newCustomersOnly', e.target.checked)}
                          className="mr-2 h-4 w-4 rounded border-[#504945] text-[#d3869b] focus:ring-[#83a598]"
                        />
                        <span className="text-xs text-[#bdae93]">New customers only</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={promo.autoRevert || false}
                          onChange={(e) => handlePromoChange(promoIndex, 'autoRevert', e.target.checked)}
                          className="mr-2 h-4 w-4 rounded border-[#504945] text-[#d3869b] focus:ring-[#83a598]"
                        />
                        <span className="text-xs text-[#bdae93]">Auto-revert to base price after promo ends</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PromoPricingManager;

